// GrindLog — Footer.tsx
// Usa las mismas variables CSS de DarkTheme.css y el hook useT de i18n

import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { Tab } from '../App';

type LegalDoc = 'terms' | 'privacy' | 'refund';

// ─── Modal de documentos legales ──────────────────────────────────────────────
function LegalModal({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  const { t } = useT();

  const renderContent = (text: string) => {
    return text.split('\n\n').map((para, i) => {
      if (/^\*\*[^*]+\*\*$/.test(para.trim())) {
        return (
          <p key={i} style={{
            fontFamily: 'var(--mono)',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase' as const,
            color: 'var(--accent-hi)',
            marginTop: i === 0 ? 0 : '1.25rem',
            marginBottom: '0.35rem',
          }}>
            {para.replace(/\*\*/g, '')}
          </p>
        );
      }
      const parts = para.split(/\*\*(.*?)\*\*/g);
      return (
        <p key={i} style={{ color: 'var(--text)', fontSize: '0.84rem', lineHeight: 1.75, marginBottom: '0.4rem' }}>
          {parts.map((part, j) =>
            j % 2 === 1
              ? <strong key={j} style={{ color: 'var(--text-hi)', fontWeight: 600 }}>{part}</strong>
              : part
          )}
        </p>
      );
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(4,6,15,0.88)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          width: '100%', maxWidth: 620,
          maxHeight: '82vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 0 60px rgba(41,121,255,0.08)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--display)', fontWeight: 800,
              fontSize: '0.95rem', color: 'var(--text-hi)', letterSpacing: '0.02em',
            }}>
              {t(`footer.${doc}Title`)}
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {t('footer.lastUpdated')}: {t('footer.lastUpdatedDate')}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid var(--border)',
              color: 'var(--text-muted)', cursor: 'pointer',
              width: 30, height: 30, borderRadius: 5,
              fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = 'var(--accent)'; b.style.color = 'var(--text-hi)'; }}
            onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = 'var(--border)'; b.style.color = 'var(--text-muted)'; }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>
          {renderContent(t(`footer.${doc}Content`))}
        </div>

        {/* Footer del modal */}
        <div style={{
          padding: '0.85rem 1.25rem',
          borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            className="btn btn-primary"
            style={{ fontSize: '0.78rem', padding: '0.4rem 1.1rem' }}
          >
            {t('footer.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
interface FooterProps {
  onNavigate: (tab: Tab) => void;
  onNavigateFaq: () => void;
}

export function Footer({ onNavigate, onNavigateFaq }: FooterProps) {
  const { t } = useT();
  const [openDoc, setOpenDoc] = useState<LegalDoc | null>(null);
  const year = new Date().getFullYear();

  const colLabel = (label: string) => (
    <span style={{
      display: 'block',
      fontFamily: 'var(--mono)', fontSize: '0.62rem', fontWeight: 700,
      letterSpacing: '0.12em', textTransform: 'uppercase' as const,
      color: 'var(--border)', marginBottom: '0.65rem',
    }}>{label}</span>
  );

  // Navegación interna — llama a onNavigate, sin href, sin recarga
  const navBtn = (label: string, tab: Tab) => (
    <button
      onClick={() => onNavigate(tab)}
      style={{
        display: 'block', background: 'none', border: 'none', padding: 0,
        fontFamily: 'var(--body)', fontSize: '0.82rem',
        color: 'var(--text-muted)', cursor: 'pointer', textAlign: 'left' as const,
        transition: 'color 0.15s', marginBottom: '0.45rem',
      }}
      onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-hi)'}
      onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'}
    >{label}</button>
  );

  // Link externo — abre en nueva pestaña
  const extLink = (label: string, href: string) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block', fontFamily: 'var(--body)', fontSize: '0.82rem',
        color: 'var(--text-muted)', textDecoration: 'none',
        transition: 'color 0.15s', marginBottom: '0.45rem',
      }}
      onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-hi)'}
      onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-muted)'}
    >{label}</a>
  );

  const legalBtn = (doc: LegalDoc, label: string, highlight = false) => (
    <button
      onClick={() => setOpenDoc(doc)}
      style={{
        display: 'block', background: 'none', border: 'none', padding: 0,
        fontFamily: 'var(--body)', fontSize: '0.82rem',
        color: highlight ? 'var(--accent-hi)' : 'var(--text-muted)',
        cursor: 'pointer', textAlign: 'left' as const,
        transition: 'color 0.15s', marginBottom: '0.45rem',
      }}
      onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-hi)'}
      onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = highlight ? 'var(--accent-hi)' : 'var(--text-muted)'}
    >{label}</button>
  );

  return (
    <>
      {openDoc && <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} />}

      <footer style={{
        background: 'var(--bg)',
        borderTop: '1px solid var(--border-dim)',
        padding: '2.25rem 2rem 1.5rem',
        marginTop: 'auto',
      }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>

          {/* Top */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '2rem', marginBottom: '1.75rem',
          }}>
            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.65rem' }}>
                <span className="brand-suit">♠</span>
                <span className="brand-name">Grind<span className="brand-accent">Log</span></span>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1.6, maxWidth: 200, margin: 0 }}>
                {t('footer.tagline')}
              </p>
            </div>

            {/* Columnas */}
            <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>
              <div>
                {colLabel(t('footer.colProduct'))}
                {navBtn(t('footer.linkDashboard'), 'dashboard')}
                {extLink(t('footer.linkPricing'), 'https://grindlog.app/#pricing')}
                {navBtn(t('footer.linkGuide'), 'guide')}
                <button
                  onClick={onNavigateFaq}
                  style={{
                    display: 'block', background: 'none', border: 'none', padding: 0,
                    fontFamily: 'var(--body)', fontSize: '0.82rem',
                    color: 'var(--text-muted)', cursor: 'pointer', textAlign: 'left' as const,
                    transition: 'color 0.15s', marginBottom: '0.45rem',
                  }}
                  onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-hi)'}
                  onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'}
                >FAQ</button>
              </div>
              <div>
                {colLabel(t('footer.colLegal'))}
                {legalBtn('terms',   t('footer.termsTitle'))}
                {legalBtn('privacy', t('footer.privacyTitle'))}
                {legalBtn('refund',  t('footer.refundTitle'), true)}
              </div>
              <div>
                {colLabel(t('footer.colSupport'))}
                {extLink('support@grindlog.app', 'mailto:support@grindlog.app')}
                {extLink('GitHub', 'https://github.com/FrancoScioli/GrindLog')}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{
            height: 1,
            background: 'linear-gradient(to right, transparent, var(--border) 20%, var(--border) 80%, transparent)',
            marginBottom: '1.1rem',
          }} />

          {/* Bottom */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                © {year} GrindLog · Franco Scioli · Buenos Aires, AR
              </span>
              <span style={{ fontSize: '0.68rem', color: 'var(--border)', maxWidth: 500, lineHeight: 1.4 }}>
                {t('footer.disclaimer')}
              </span>
            </div>

            {/* Redes */}
            <div style={{ display: 'flex', gap: '0.45rem' }}>
              {([
                { label: '𝕏', href: 'https://twitter.com/grindlog_gg', title: '@grindlog_gg on X' },
                {
                  label: '◻', href: 'https://instagram.com/grindlog_gg', title: '@grindlog_gg en Instagram',
                  svg: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
                },
              ] as Array<{ label: string; href: string; title: string; svg?: JSX.Element }>).map(({ label, href, title, svg }, i) => (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer" title={title}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28,
                    border: '1px solid var(--border)', borderRadius: 5,
                    color: 'var(--text-muted)', textDecoration: 'none',
                    fontSize: '0.82rem', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { const a = e.currentTarget as HTMLAnchorElement; a.style.color = 'var(--accent-hi)'; a.style.borderColor = 'var(--accent)'; a.style.background = 'var(--accent-glow)'; }}
                  onMouseLeave={e => { const a = e.currentTarget as HTMLAnchorElement; a.style.color = 'var(--text-muted)'; a.style.borderColor = 'var(--border)'; a.style.background = 'transparent'; }}
                >
                  {svg ?? label}
                </a>
              ))}
            </div>
          </div>

        </div>
      </footer>
    </>
  );
}
